import { getImageProps } from 'next/image';
import { useMedia } from 'react-use';
import { UmbracoTypes } from '~/lib/data-contract';
import { LinkButton } from '~/shared/components/Button';
import { umbracoLoader } from '~/shared/components/Image/loaders/umbracoLoader';
import { RawHtml } from '~/shared/components/RawHtml';
import { Spacer } from '~/shared/components/Spacer';
import { Video } from '~/shared/components/Video';
import { AlignContent } from '~/templates/blocks/shared/AlignContent';
import { Headline } from '~/templates/blocks/shared/Headline';
import { breaks, queries } from '~/theme/breakpoints';
import { useModule } from '../../hooks/useModule';
import { useModuleTheme } from '../../hooks/useModuleTheme';
import { ModuleContainer } from '../../shared/ModuleContainer';
import getFocalPoint from '../../utils/getFocalPoint';
import { StyledM75, StyledM75Content, StyledMediaArea } from './styled';
import { M75MediaTextProps } from './types';

export type { M75MediaTextProps } from './types';

export const M75MediaText = ({
    image,
    video,
    headline,
    description,
    callToAction,
    mediaSize = 'default',
    mediaHorizontalAlignment = 'left',
    horizontalAlignment = 'left',
    verticalAlignment = 'center',
    backgroundColor: initialBackgroundColor,
    backgroundColorHEX: initialBackgroundColorHex,
    callToActionTypeFill,
    hasGap = true,
    maxHeight = UmbracoTypes.M75MediaModuleMaxHeight.Medium,
    imageMobile,
    ...rest
}: M75MediaTextProps) => {
    const { index } = useModule();
    const { textColor, backgroundColor, buttonColor, buttonVariant } = useModuleTheme({
        backgroundColor: initialBackgroundColorHex || initialBackgroundColor,
        buttonVariant: callToActionTypeFill ? 'fill' : 'stroke',
    });

    const mediaType = video ? 'video' : 'image';

    const isTabletUp = useMedia(queries.md, false);

    const desktopSrc = image?.src ?? imageMobile?.src;
    const mobileSrc = imageMobile?.src ?? image?.src;

    const {
        props: { srcSet: desktop, ...desktopProps },
    } = getImageProps({
        loader: umbracoLoader,
        alt: image?.alt ?? headline ?? 'Media Text Image Desktop',
        src: desktopSrc ?? '',
        fill: true,
        sizes: `${breaks.pageMax}px`,
        loading: index < 2 ? 'eager' : 'lazy',
        style: {
            objectFit: 'cover',
            objectPosition: getFocalPoint(image),
        },
    });

    const {
        props: { srcSet: mobile, ...mobileProps },
    } = getImageProps({
        loader: umbracoLoader,
        fill: true,
        alt: imageMobile?.alt ?? headline ?? 'Media Text Image Mobile',
        src: mobileSrc ?? '',
        loading: index < 2 ? 'eager' : 'lazy',
        sizes: '80vw',
        style: {
            objectFit: 'cover',
            objectPosition: getFocalPoint(imageMobile),
        },
    });

    return (
        <ModuleContainer hasGutter {...rest}>
            <StyledM75>
                <StyledM75Content
                    backgroundColor={backgroundColor}
                    mediaSize={mediaSize}
                    mediaAlignment={mediaHorizontalAlignment}
                    mediaType={mediaType}
                    hasGap={hasGap}
                    maxHeight={maxHeight}
                >
                    <AlignContent
                        alignment={horizontalAlignment}
                        position={verticalAlignment}
                        textColor={textColor}
                    >
                        {headline ? <Headline variant="display2">{headline}</Headline> : null}
                        {headline ? <Spacer size="4" /> : null}
                        {description ? <RawHtml html={description} /> : null}
                        {callToAction ? <Spacer size="2" customFrom={{ '1000px': '4' }} /> : null}
                        {callToAction ? (
                            <LinkButton
                                href={callToAction.url}
                                target={callToAction.target}
                                variant={buttonVariant}
                                color={buttonColor}
                            >
                                {callToAction.text}
                            </LinkButton>
                        ) : null}
                    </AlignContent>
                    <StyledMediaArea mediaType={mediaType}>
                        {desktopSrc ? (
                            <picture>
                                <source
                                    media={imageMobile?.src ? `${queries.md}` : ''}
                                    srcSet={desktop}
                                />
                                {imageMobile?.src && <source srcSet={mobile} />}
                                <img
                                    {...(isTabletUp ? desktopProps : mobileProps)}
                                    alt={imageMobile?.alt ?? headline}
                                />
                            </picture>
                        ) : null}
                        {mediaType === 'video' && video ? (
                            <Video
                                controls={false}
                                src={video.src}
                                loop={video.loop}
                                playing={video.autoPlay}
                                posterSrc={image?.src}
                            />
                        ) : null}
                    </StyledMediaArea>
                </StyledM75Content>
            </StyledM75>
        </ModuleContainer>
    );
};
